<template>
  <section
    class="user-comment-list-wrapper--container"
    data-automation-id="wrapper_user_comment_list"
  >
    <div
      :class="{ disabled: disabled }"
      class="user-comment-list-wrapper--comment"
    >
      <div
        v-if="discussionTypeId == 2"
        class="user-comment-list-wrapper--chips"
      >
        <asc-chip-button
          v-model="isCommentsActive"
          class="user-comment-list-wrapper--chip"
          :button-text="commentsButtonText"
        />
        <asc-chip-button
          v-model="isActivityActive"
          class="user-comment-list-wrapper--chip"
          :button-text="activityButtonText"
        />
        <!--- Re-enable once documents are included in Recent Activity
        <asc-chip-button
          v-model="isDocumentsActive"
          class="user-comment-list-wrapper--chip"
          :button-text="documentsButtonText"
        />
        -->
      </div>
      <asc-user-comment
        :is-user="isUser"
        :new-comment="true"
        :reset-comment="resetComment"
        :user="user"
        :user-list="userList"
        :disabled="disabled"
        :editor-placeholder="editorPlaceholder"
        :has-post-btn="hasPostBtn"
        :helper-text="helperText"
        :is-expanded="isExpanded"
        :comment-change-event-on-blur="commentChangeEventOnBlur"
        @post="handleNewComment"
        @onPostClick="handlePostClick"
        @clearOutComment="handleClearOutComment"
        @hasData="handleHasData"
        @commentText="handlecommentText"
      />
    </div>
    <asc-user-comment-list
      :key="`user-comment-list--${_uid}-${key}`"
      :user-comments="commentList"
      :user="user"
      :user-list="userList"
      :status-updates="statusUpdates"
      :disabled="disabled"
      :max-list-height="getMaxListHeight"
      :is-expanded="isExpanded"
      :comment-change-event-on-blur="true"
      :active-filters="activeFilters"
      @removeComment="handleCommentRemoval"
      @updatedComment="handleUpdatedComment"
    />
  </section>
</template>

<script>
import { AscUserCommentPropsMixin } from '@/components/mixins'
import AscUserComment from '@/components/molecules/AscUserComment'
import AscUserCommentList from '@/components/molecules/AscUserCommentList'
import AscChipButton from '../atoms/AscChipButton.vue'

export default {
  name: 'AscUserCommentListWrapper',

  components: {
    AscUserComment,
    AscUserCommentList,
    AscChipButton,
  },

  mixins: [AscUserCommentPropsMixin],

  props: {
    isExpanded: {
      type: Boolean,
      default: false,
    },
    discussionTypeId: {
      type: Number,
      default: -1,
    },
    resetComment: {
      type: Boolean,
      default: false,
    },
    restoreDiscussions: {
      type: Boolean,
      default: false,
    },
    editorPlaceholder: {
      type: String,
      default: 'Add a comment.',
    },
    helperText: {
      type: String,
      default: '',
    },
    hasPostBtn: {
      type: Boolean,
      default: false,
    },
    commentChangeEventOnBlur: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      commentList: this.userComments,
      key: 0,
      commentsButtonText: 'Comments',
      activityButtonText: 'Activity',
      documentsButtonText: 'Documents',
      isCommentsActive: true, // default state is only comments are active
      isActivityActive: false,
      isDocumentsActive: false,
    }
  },

  computed: {
    getMaxListHeight() {
      // editor height 34px
      // padding of 32px
      return this.maxListHeight - 34 - 32
    },
    activeFilters() {
      const filters = []
      if (this.isCommentsActive) filters.push('userComment')
      if (this.isActivityActive) filters.push('statusUpdate')
      if (this.isDocumentsActive) filters.push('document')
      return filters
    },
  },

  watch: {
    userComments(newValue) {
      this.commentList = newValue
    },
    restoreDiscussions(newValue) {
      if (newValue) {
        this.key++
      }
    },
  },

  methods: {
    handleCommentRemoval(id) {
      this.$emit('deleteComment', {
        whichComment: id,
        discussionType: this.discussionTypeId,
      })
    },
    handleNewComment(comment) {
      comment.isNew = true
      this.$emit('addComment', {
        comment: comment,
        discussionType: this.discussionTypeId,
      })
    },
    handleHasData(data) {
      this.$emit('hasData', data)
    },
    handlecommentText(comment) {
      this.$emit('commentText', {
        comment: comment,
        discussionType: this.discussionTypeId,
      })
    },
    handlePostClick(comment) {
      comment.isNew = true
      this.$emit('onPostClick', {
        comment: comment,
        discussionType: this.discussionTypeId,
      })
    },
    handleClearOutComment(comment) {
      // this covers an instance where a user enters a new comment, deletes
      // all the text out (without saving) and THEN saves.  We need to emit an event
      // up to the container to clear it out
      const isCommentAlreadySaved = this.userComments.filter(commentObj => {
        return commentObj.id === comment.id
      })
      if (!isCommentAlreadySaved.length) {
        // if you're just blanking out an unsaved comment, notify the container
        // so that it doesn't get saved
        this.$emit('removeUnsavedComment', {
          comment: comment,
          discussionType: this.discussionTypeId,
        })
      }
    },
    handleUpdatedComment(comment) {
      // if the comment is already marked as new, even if the user
      // updates it for our purposes it's still new
      if (!comment.isNew) {
        comment.isUpdated = true
      }
      this.$emit('updateComment', {
        comment: comment,
        discussionType: this.discussionTypeId,
      })
    },
  },
}
</script>

<style lang="scss">
.user-comment-list-wrapper {
  &--container {
    overflow: hidden;
  }

  &--comment {
    padding: 0 16px;
    margin-bottom: 4px;

    &.disabled {
      margin-bottom: 0;
    }
  }

  &--chips {
    margin-bottom: 16px;
    display: flex;
    gap: 5px;
  }
}
</style>
